import { useState, type ReactElement } from 'react'
import { useAuth, peutEcrire } from '../lib/auth'
import { EtatSync } from '../components/EtatSync'
import {
  IconeDevis,
  IconeInfo,
  IconePhotos,
  IconePlans,
  IconeSuivis,
  LogoComplet
} from '../components/Icones'
import type { Chantier as TChantier } from '../lib/types'
import { Photos } from './Photos'
import { Plans } from './Plans'
import { Info } from './Info'
import { AVenir } from './AVenir'

type Onglet = 'photos' | 'plans' | 'devis' | 'suivis' | 'info'

const ONGLETS: { cle: Onglet; libelle: string; Icone: () => ReactElement }[] = [
  { cle: 'info', libelle: 'Info', Icone: IconeInfo },
  { cle: 'photos', libelle: 'Photos', Icone: IconePhotos },
  { cle: 'plans', libelle: 'Plans', Icone: IconePlans },
  { cle: 'devis', libelle: 'Devis', Icone: IconeDevis },
  { cle: 'suivis', libelle: 'Suivis', Icone: IconeSuivis }
]

export function Chantier({
  chantier,
  fermer,
  recharger
}: {
  chantier: TChantier
  fermer: () => void
  recharger: () => void
}) {
  const { profil } = useAuth()
  const [onglet, setOnglet] = useState<Onglet>('info')
  const [camera, setCamera] = useState(false)
  const ecrivable = peutEcrire(profil, chantier.statut)

  // Bouton de création contextuel (décision d'ergonomie no 3) : il n'existe
  // que là où l'onglet répond déjà à la question « créer quoi ».
  const fabVisible = ecrivable && onglet === 'photos'

  return (
    <div className="app">
      <header className="entete">
        <div className="logo-centre petit">
          <LogoComplet hauteur={30} />
        </div>
        <div className="entete-rang">
          <button className="retour" onClick={fermer}>
            ‹ Chantiers
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {chantier.nom}
            </h1>
            <p className="sous">
              {chantier.numero}
              {chantier.adresse ? ` · ${chantier.adresse}` : ''}
            </p>
          </div>
          <EtatSync />
        </div>
      </header>

      <div className="corps">
        {chantier.statut === 'termine' && (
          <p className="avis" style={{ marginBottom: 14 }}>
            Chantier archivé — lecture seule. Rien ne peut plus y être ajouté ni modifié.
          </p>
        )}

        {onglet === 'photos' && (
          <Photos chantier={chantier} ouvrirCamera={camera} setOuvrirCamera={setCamera} />
        )}
        {onglet === 'plans' && <Plans chantier={chantier} />}
        {onglet === 'devis' && (
          <AVenir
            titre="Devis"
            phase="Phase 2"
            description="Le cahier des charges, les addenda et les directives de changement, avec recherche plein texte et sections suivies. Cette section arrive à la phase 2."
          />
        )}
        {onglet === 'suivis' && (
          <AVenir
            titre="Suivis"
            phase="Phase 3"
            description="Tâches, déficiences et QRT : assignation, échéances, rappels automatiques et validation avant fermeture. Cette section arrive à la phase 3."
          />
        )}
        {onglet === 'info' && <Info chantier={chantier} recharger={recharger} />}
      </div>

      {fabVisible && (
        <button className="fab" onClick={() => setCamera(true)} aria-label="Prendre une photo">
          +
        </button>
      )}

      <nav className="onglets" role="tablist" aria-label="Sections du chantier">
        {ONGLETS.map(({ cle, libelle, Icone }) => (
          <button
            key={cle}
            role="tab"
            aria-selected={onglet === cle}
            onClick={() => setOnglet(cle)}
          >
            <Icone />
            {libelle}
            <span className="pip" />
          </button>
        ))}
      </nav>
    </div>
  )
}
